import Axios from 'axios'
import DayJS from '../../../../helpers/DayJS'
import { buildPathWithQuery } from '../../../../helpers/urls'
import type {
    AIReportsAlgorithmsBySessionResponse,
    AIReportsSessionSummaryResponse,
    AIReportsUsageResponse,
    getAIReportsBySessionIdResponse,
    getConsolidatedAIReportResponse,
    getConsolidatedAIReportHTMLResponse,
    FetchRequestsReportDataResponse,
} from './api-reports.types'

const baseURL = `${process.env.REACT_APP_REPORTS}v1/dashboard/`

const tz = DayJS.tz.guess()

export const ApiReports = Axios.create({
    baseURL,
    headers: { tz },
})

export const getCohorts = (courseId: string) =>
    ApiReports.get<GetCohortsResponse>(`cohorts/list/${courseId}`).then(({ data }) => data.data)

export const getDailyMonitoring = ({ courseId, cohorts, from, to }: DateFilterProps) =>
    ApiReports.get(`sessions/db/csv/${courseId}?cohorts=${cohorts}&from=${from}&to=${to}`).then((data) => data)

export const getUsersGraph = ({ courseId, cohorts }: BasicProps) =>
    ApiReports.get<GetUsersGraphResponse>(`cohorts/graphs/${courseId}?cohorts=${cohorts}`).then(({ data }) => data.data)

export const getUsersCsv = ({ courseId, cohorts }: BasicProps) =>
    ApiReports.get(`cohorts/csv/${courseId}?cohorts=${cohorts}`).then((data) => data)

export const getAttendanceGraph = ({ courseId, cohorts, from, to }: DateFilterProps) =>
    ApiReports.get<GetAttendanceGraphResponse>(
        `attendances/graphs/${courseId}?cohorts=${cohorts}&from=${from}&to=${to}`
    ).then(({ data }) => data.data)

export const getAttendanceCsv = ({ courseId, cohorts, from, to }: DateFilterProps) =>
    ApiReports.get(`attendances/csv/${courseId}?cohorts=${cohorts}&from=${from}&to=${to}`).then((data) => data)

export const getParticipationGraph = ({ courseId, cohorts, from, to }: DateFilterProps) =>
    ApiReports.get<GetParticipationGraphResponse>(
        `participation/graphs/${courseId}?cohorts=${cohorts}&from=${from}&to=${to}`
    ).then(({ data }) => data.data)

export const getParticipationCsv = ({ courseId, cohorts, from, to }: DateFilterProps) =>
    ApiReports.get(`participation/csv/${courseId}?cohorts=${cohorts}&from=${from}&to=${to}`).then((data) => data)

export const getAudioParticipationCsv = ({ courseId, cohorts, from, to }: DateFilterProps) =>
    ApiReports.get(`participation/audio/csv/${courseId}?cohorts=${cohorts}&from=${from}&to=${to}`).then((data) => data)

export const getValuationGraph = ({ courseId, cohorts, from, to }: DateFilterProps) =>
    ApiReports.get<GetValuationGraphResponse>(
        `course/assessment/feedback/graphs/${courseId}?cohorts=${cohorts}&from=${from}&to=${to}`
    ).then(({ data }) => data.data)

export const getValuationCsv = ({ courseId, cohorts, from, to }: DateFilterProps) =>
    ApiReports.get(`course/assessment/feedback/csv/${courseId}?cohorts=${cohorts}&from=${from}&to=${to}`).then(
        (data) => data
    )

export const getValuationComments = ({ courseId, cohorts, date, score }: ValuationProps) =>
    ApiReports.get<GetCSATCommentsResponse>(
        `course/assessment/feedback/graphs/tables/${courseId}?cohorts=${cohorts}&dates=${date}&scores=${score}`
    ).then(({ data }) => data.data.commentTableGraph)

export const getPulsesGraph = ({ courseId, cohorts, from, to }: DateFilterProps) =>
    ApiReports.get<GetPulsesGraphResponse>(
        `course/pulses/utilization/graphs/${courseId}?cohorts=${cohorts}&from=${from}&to=${to}`
    ).then(({ data }) => data.data)

export const getPulsesCsv = ({ courseId, cohorts, from, to }: DateFilterProps) =>
    ApiReports.get(`course/pulses/utilization/csv/${courseId}?cohorts=${cohorts}&from=${from}&to=${to}`).then(
        (data) => data
    )

export const getPulsesRanking = ({ courseId, cohorts, from, to }: DateFilterProps) =>
    ApiReports.get<GetPulsesRankingResponse>(
        `course/pulses/ranking/graphs/${courseId}?cohorts=${cohorts}&from=${from}&to=${to}`
    ).then(({ data }) => data.data)

export const getNPSGraph = ({ courseId, cohorts, from, to }: DateFilterProps) =>
    ApiReports.get<GetNPSGraphResponse>(
        `course/assessment/nps/graphs/${courseId}?cohorts=${cohorts}&from=${from}&to=${to}`
    ).then(({ data }) => data.data)

export const getNPSCsv = ({ courseId, cohorts, from, to }: DateFilterProps) =>
    ApiReports.get(`course/assessment/nps/csv/${courseId}?cohorts=${cohorts}&from=${from}&to=${to}`).then(
        (data) => data
    )

export const fetchMentorAIUsageData = async ({ courseId, ...params }: DateFilterProps) => {
    const path = buildPathWithQuery(`mentor-ai/questions/graphs/${courseId}`, params)
    return ApiReports.get<AIReportsUsageResponse>(path).then(({ data }) => data.data)
}

export const fetchMentorAIUsageCsv = async ({ courseId, ...params }: DateFilterProps) => {
    const path = buildPathWithQuery(`mentor-ai/csv/${courseId}`, params)
    return ApiReports.get<string>(path).then(({ data }) => data)
}

export const getAlgorithmsByRange = async ({ courseId, ...params }: DateFilterProps) => {
    const path = buildPathWithQuery(`mentor-ai/${courseId}/sessions`, params)
    return ApiReports.get<AIReportsAlgorithmsBySessionResponse>(path).then(({ data }) => data.data.sessions)
}

export const getAIReportsSessionsByRange = async ({ courseId, algorithmId, ...params }: RageWithAlgorithmId) => {
    const path = buildPathWithQuery(`mentor-ai/${courseId}/sessions/${algorithmId}`, params)
    return ApiReports.get<AIReportsSessionSummaryResponse>(path).then(({ data }) => data.data[0]?.tables)
}

export const getAIReportsTablesBySessionId = ({
    courseId,
    algorithmId,
    sessionId,
}: {
    courseId: string
    algorithmId: string
    sessionId: string
}) =>
    ApiReports.get<getAIReportsBySessionIdResponse>(
        `mentor-ai/${courseId}/sessions/${algorithmId}/${sessionId}/reports`
    ).then(({ data }) => data.data)

export const getConsolidatedAIReport = ({ courseId, algorithmId }: { courseId: string; algorithmId: string }) =>
    ApiReports.get<getConsolidatedAIReportResponse>(
        `mentor-ai/${courseId}/sessions/${algorithmId}/consolidated-report`
    ).then(({ data }) => data.data)

export const getConsolidatedAIReportHTML = ({ courseId, algorithmId }: { courseId: string; algorithmId: string }) =>
    ApiReports.get<getConsolidatedAIReportHTMLResponse>(
        `mentor-ai/${courseId}/sessions/${algorithmId}/consolidated-report/html`
    ).then(({ data }) => data.data)

export const fetchRequestsReportData = async ({ courseId, ...params }: DateFilterProps) => {
    const path = buildPathWithQuery(`help-request/chart/${courseId}`, params)
    return ApiReports.get<FetchRequestsReportDataResponse>(path).then(({ data }) => data.data)
}

export const fetchRequestsReportCsv = async ({ courseId, ...params }: DateFilterProps) => {
    const path = buildPathWithQuery(`help-request/csv/${courseId}`, params)
    return ApiReports.get<string>(path).then(({ data }) => data)
}

// ===== NETWORK ENDPOINTS =====
// These endpoints aggregate data across multiple cohorts within a network
// Note: cohorts parameter contains courseIds (MongoDB IDs), NOT dates

export const getNetworkCohorts = (networkId: string) =>
    ApiReports.get<GetNetworkCohortsResponse>(`network/cohorts/list/${networkId}`).then(({ data }) => data.data)

export const getNetworkUsersGraph = ({ networkId, cohorts }: NetworkBasicProps) =>
    ApiReports.get<GetUsersGraphResponse>(`network/cohorts/graphs/${networkId}?cohorts=${cohorts.join(',')}`).then(
        ({ data }) => data.data
    )

export const getNetworkUsersCSV = ({ networkId, cohorts }: NetworkBasicProps) =>
    ApiReports.get(`network/cohorts/csv/${networkId}?cohorts=${cohorts.join(',')}`).then((data) => data)

export const getNetworkAttendanceGraph = ({ networkId, cohorts, from, to }: NetworkDateFilterProps) =>
    ApiReports.get<GetAttendanceGraphResponse>(
        `network/attendances/graphs/${networkId}?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`
    ).then(({ data }) => data.data)

export const getNetworkAttendanceCSV = ({ networkId, cohorts, from, to }: NetworkDateFilterProps) =>
    ApiReports.get(`network/attendances/csv/${networkId}?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`).then(
        (data) => data
    )

export const getNetworkParticipationGraph = ({ networkId, cohorts, from, to }: NetworkDateFilterProps) =>
    ApiReports.get<GetParticipationGraphResponse>(
        `network/participation/graphs/${networkId}?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`
    ).then(({ data }) => data.data)

export const getNetworkParticipationCSV = ({ networkId, cohorts, from, to }: NetworkDateFilterProps) =>
    ApiReports.get(`network/participation/csv/${networkId}?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`).then(
        (data) => data
    )

export const getNetworkAudioParticipationCSV = ({ networkId, cohorts, from, to }: NetworkDateFilterProps) =>
    ApiReports.get(
        `network/participation/audio/csv/${networkId}?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`
    ).then((data) => data)

export const getNetworkValuationGraph = ({ networkId, cohorts, from, to }: NetworkDateFilterProps) =>
    ApiReports.get<GetValuationGraphResponse>(
        `network/assessment/feedback/graphs/${networkId}?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`
    ).then(({ data }) => data.data)

export const getNetworkValuationCSV = ({ networkId, cohorts, from, to }: NetworkDateFilterProps) =>
    ApiReports.get(
        `network/assessment/feedback/csv/${networkId}?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`
    ).then((data) => data)

export const getNetworkValuationComments = ({ networkId, cohorts, date, score }: NetworkValuationProps) =>
    ApiReports.get<GetCSATCommentsResponse>(
        `network/assessment/feedback/graphs/tables/${networkId}?cohorts=${cohorts.join(',')}&dates=${date.join(',')}&scores=${score.join(',')}`
    ).then(({ data }) => data.data.commentTableGraph)

export const getNetworkNPSGraph = ({ networkId, cohorts, from, to }: NetworkDateFilterProps) =>
    ApiReports.get<GetNPSGraphResponse>(
        `network/assessment/nps/graphs/${networkId}?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`
    ).then(({ data }) => data.data)

export const getNetworkNPSCSV = ({ networkId, cohorts, from, to }: NetworkDateFilterProps) =>
    ApiReports.get(`network/assessment/nps/csv/${networkId}?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`).then(
        (data) => data
    )

export const getNetworkPulsesGraph = ({ networkId, cohorts, from, to }: NetworkDateFilterProps) =>
    ApiReports.get<GetPulsesGraphResponse>(
        `network/pulses/utilization/graphs/${networkId}?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`
    ).then(({ data }) => data.data)

export const getNetworkPulsesCSV = ({ networkId, cohorts, from, to }: NetworkDateFilterProps) =>
    ApiReports.get(
        `network/pulses/utilization/csv/${networkId}?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`
    ).then((data) => data)

export const getNetworkPulsesRanking = ({ networkId, cohorts }: NetworkBasicProps) =>
    ApiReports.get<GetPulsesRankingResponse>(
        `network/pulses/ranking/graphs/${networkId}?cohorts=${cohorts.join(',')}`
    ).then(({ data }) => data.data)

export const fetchNetworkMentorAIUsageData = async ({ networkId, cohorts, from, to }: NetworkDateFilterProps) => {
    const path = `network/mentor-ai/questions/graphs/${networkId}?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`
    return ApiReports.get<AIReportsUsageResponse>(path).then(({ data }) => data.data)
}

export const fetchNetworkMentorAIUsageCSV = async ({ networkId, cohorts, from, to }: NetworkDateFilterProps) => {
    const path = `network/mentor-ai/csv/${networkId}?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`
    return ApiReports.get<string>(path).then(({ data }) => data)
}

export const getNetworkAlgorithmsByRange = async ({ networkId, cohorts, from, to }: NetworkDateFilterProps) => {
    const path = `network/mentor-ai/${networkId}/sessions?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`
    return ApiReports.get<AIReportsAlgorithmsBySessionResponse>(path).then(({ data }) => data.data.sessions)
}

export const getNetworkAIReportsSessionsByRange = async ({
    networkId,
    cohorts,
    algorithmId,
    from,
    to,
}: NetworkDateFilterProps & { algorithmId: string }) => {
    const path = `network/mentor-ai/${networkId}/sessions/${algorithmId}?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`
    return ApiReports.get<AIReportsSessionSummaryResponse>(path).then(({ data }) => data.data[0]?.tables)
}

export const getNetworkAIReportsTablesBySessionId = ({
    networkId,
    algorithmId,
    sessionId,
}: {
    networkId: string
    algorithmId: string
    sessionId: string
}) =>
    ApiReports.get<getAIReportsBySessionIdResponse>(
        `network/mentor-ai/${networkId}/sessions/${algorithmId}/${sessionId}/reports`
    ).then(({ data }) => data.data)

export const fetchNetworkRequestsReportData = async ({ networkId, cohorts, from, to }: NetworkDateFilterProps) => {
    const path = `network/help-request/chart/${networkId}?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`
    return ApiReports.get<FetchRequestsReportDataResponse>(path).then(({ data }) => data.data)
}

export const fetchNetworkRequestsReportCSV = async ({ networkId, cohorts, from, to }: NetworkDateFilterProps) => {
    const path = `network/help-request/csv/${networkId}?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`
    return ApiReports.get<string>(path).then(({ data }) => data)
}

export const getNetworkDailyMonitoring = ({ networkId, cohorts, from, to }: NetworkDateFilterProps) =>
    ApiReports.get(`network/sessions/db/csv/${networkId}?cohorts=${cohorts.join(',')}&from=${from}&to=${to}`).then(
        (data) => data
    )

// ===== TYPE DEFINITIONS =====

export type BasicProps = {
    courseId: string
    cohorts: string[]
}
export type DateFilterProps = {
    from?: string
    to?: string
} & BasicProps

type RageWithAlgorithmId = DateFilterProps & { algorithmId: string }

export type ValuationProps = {
    date: string[]
    score: string[]
} & BasicProps

// Network types (cohorts are courseIds, not dates)
export type NetworkBasicProps = {
    networkId: string
    cohorts: string[] // courseIds (MongoDB IDs)
}

export type NetworkDateFilterProps = {
    from?: string
    to?: string
} & NetworkBasicProps

export type NetworkValuationProps = {
    date: string[]
    score: string[]
} & NetworkBasicProps

export interface API_RESPONSE<TData> {
    message: string
    code: 200 | 401 | 403 | 404 | 409 | 500
    success: boolean
    data: TData
}
type GetCohortsResponse = API_RESPONSE<CohortsData>
type GetNetworkCohortsResponse = API_RESPONSE<NetworkCohortsData>
type GetUsersGraphResponse = API_RESPONSE<UsersData>
type GetAttendanceGraphResponse = API_RESPONSE<AttendanceData>
type GetParticipationGraphResponse = API_RESPONSE<ParticipationData>
type GetValuationGraphResponse = API_RESPONSE<ValuationData>
type GetPulsesGraphResponse = API_RESPONSE<PulsesData>
type GetPulsesRankingResponse = API_RESPONSE<PulsesRakingData[]>
type GetCSATCommentsResponse = API_RESPONSE<CSATComments>
type GetNPSGraphResponse = API_RESPONSE<NPSData>

export interface CohortsData {
    cohorts: string[]
    lastMigrationDate: string
}

export interface NetworkCohortsData {
    cohorts: Array<{
        courseIdMongo: string
        name: string
        startDate: string
    }>
    lastMigrationDate: string
}

export interface UsersData {
    activeDataChart: (number | string)[][]
    enrolledChart: (number | string)[][]
}
interface AttendanceData {
    barChart: (number | string)[][]
    totalPieChart: (number | string)[][]
}
interface ParticipationData {
    lineChart: (null | number | string | string | string)[][]
    audioLineChart: (null | number | string | string | string)[][]
}

interface ValuationData {
    csatBarChart: (number | string)[][]
    pieChart: (number | string)[][]
    scoreBarChart: (number | string)[][]
}
interface PulsesData {
    barChart: (number | string)[][]
    totalPulses: number
    totalPulsesBarChart: (number | string)[][]
}
interface PulsesRakingData {
    position: number
    email: string
    pulsesIn: number
    urlImage: string
    displayName: string
}

interface CSATComments {
    commentTableGraph: (number | string)[][]
}
interface NPSData {
    cohortSize: number
    detractors: number
    detractorsPercentage: number
    nps: number
    passives: number
    passivesPercentage: number
    promoters: number
    promotersPercentage: number
    totalResponses: number
}
